// Phase 15 Plan 08 Task 1 — Profile screen (own + public + 404 states).
// Phase 19 Plan 03 Task 2 — META-04 + DEC-029 cleanup.
// Phase 17 Plan 06 Task 1 (cleanup-reskin) — re-skin to prototype tokens +
// Btn + Chip primitives; inline literals → var(--space-*) / var(--radius-*) /
// var(--font-size-*); preserves all routes, handlers, and test selectors.
//
// Single /profile/{userId} route. Three render branches:
//   1. `is_self === true`   → own profile (Edit-name CTA, ladder ELO grid)
//   2. `is_self === false`  → public profile (Challenge CTA, opponent stats)
//   3. 404 from server      → not-found empty state + back-to-lobby link
//
// CRITICAL T-15-34 mitigation: `is_self` is server-computed. The UI MUST
// consume the boolean verbatim — NEVER compare $authBearer.user_uuid to the
// URL param client-side.

import { useStore } from '@nanostores/react'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle'
import { ApiError } from '../../net/client'
import type { LadderKey } from '../../net/leaderboard'
import { fetchProfileById, type PublicProfileResponse } from '../../net/profile'
import { useRouter } from '../../router/router'
import {
  $activeMatchCap,
  $activeMatchCount,
} from '../../stores/activeMatchCount'
import { $authBearer } from '../../stores/auth'
import { $locale, type Locale } from '../../stores/locale'
import { getCachedProfile, setCachedProfile } from '../../stores/profileCache'
import { Avatar } from '../components/Avatar'
import { Btn } from '../components/Btn'
import { Chip } from '../components/Chip'
import { Screen, ScreenHeader } from '../components/Screen.js'
import { FriendCodeModal } from '../lobby/FriendCodeModal'
import { AchievementsGrid } from './AchievementsGrid'
import { EditNameModal } from './EditNameModal'
import { InventoryGrid } from './InventoryGrid'
import { LadderEloCard } from './LadderEloCard'

interface ProfileScreenProps {
  readonly userId: string
}

interface LoadState {
  readonly kind: 'loading' | 'loaded' | 'notfound' | 'error'
  readonly profile?: PublicProfileResponse
}

const LADDER_KEYS_BY_LOCALE: Record<Locale, ReadonlyArray<LadderKey>> = {
  en: ['standard_4_en', 'open_3_en', 'purist_en'],
  sv: ['standard_4_sv', 'open_3_sv', 'purist_sv'],
}

export function ProfileScreen(props: ProfileScreenProps): JSX.Element {
  const { userId } = props
  const router = useRouter()
  const bearer = useStore($authBearer)
  const activeMatchCount = useStore($activeMatchCount)
  const activeMatchCap = useStore($activeMatchCap)
  const locale = useStore($locale)
  const intl = useIntl()

  const [state, setState] = useState<LoadState>(() => {
    const cached = getCachedProfile(userId)
    return cached ? { kind: 'loaded', profile: cached } : { kind: 'loading' }
  })
  const [editOpen, setEditOpen] = useState(false)
  const [challengeOpen, setChallengeOpen] = useState(false)

  const loadedForUserRef = useRef<string | null>(
    getCachedProfile(userId) ? userId : null,
  )

  useEffect(() => {
    if (loadedForUserRef.current === userId) return
    let cancelled = false
    setState({ kind: 'loading' })
    void fetchProfileById(userId)
      .then((data) => {
        if (cancelled) return
        setCachedProfile(userId, data)
        loadedForUserRef.current = userId
        setState({ kind: 'loaded', profile: data })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: 'notfound' })
          return
        }
        setState({ kind: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  useEffect(() => {
    const onNameChanged = (): void => {
      const fresh = getCachedProfile(userId)
      if (fresh) {
        setState({ kind: 'loaded', profile: fresh })
      } else {
        void fetchProfileById(userId)
          .then((data) => {
            setCachedProfile(userId, data)
            setState({ kind: 'loaded', profile: data })
          })
          .catch(() => undefined)
      }
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('runegambit:profile-name-changed', onNameChanged)
    }
    return (): void => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(
          'runegambit:profile-name-changed',
          onNameChanged,
        )
      }
    }
  }, [userId])

  const handleEditClick = useCallback(() => setEditOpen(true), [])
  const handleEditClose = useCallback(() => setEditOpen(false), [])
  const handleChallengeOpen = useCallback(() => setChallengeOpen(true), [])
  const handleChallengeClose = useCallback(() => setChallengeOpen(false), [])
  const handleBackToLobby = useCallback(() => {
    router.navigate('/lobby')
  }, [router])

  const loadedPublicProfile =
    state.kind === 'loaded' &&
    state.profile !== undefined &&
    state.profile.is_self !== true
      ? state.profile
      : null
  const titleMessageId = loadedPublicProfile
    ? 'doc.title.profile_public'
    : 'doc.title.profile'
  const titleValues = loadedPublicProfile
    ? { username: loadedPublicProfile.display_name }
    : undefined
  useDocumentTitle(titleMessageId, titleValues)

  // Canonical back-header — present in every load state so the back control is
  // always reachable. Title defaults to "Profile"; the loaded public branch
  // swaps in the player's display name.
  const renderHeader = (title: ReactNode): JSX.Element => (
    <ScreenHeader
      title={title}
      onBack={handleBackToLobby}
      backLabel={intl.formatMessage({ id: 'nav.back', defaultMessage: 'Back' })}
    />
  )
  const profileTitle = (
    <FormattedMessage id="social.profile.title" defaultMessage="Profile" />
  )

  if (state.kind === 'loading') {
    return (
      <Screen
        name="profile"
        gutter={18}
        contentStyle={{ gap: 'var(--space-4)', alignItems: 'center' }}
        mainProps={{ 'data-state': 'loading' }}
        header={renderHeader(profileTitle)}
      >
        <p
          role="status"
          aria-live="polite"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <FormattedMessage
            id="social.profile.loading"
            defaultMessage="Loading profile…"
          />
        </p>
      </Screen>
    )
  }

  if (state.kind === 'notfound') {
    return (
      <Screen
        name="profile"
        gutter={18}
        contentStyle={{ gap: 'var(--space-4)', alignItems: 'center' }}
        mainProps={{ 'data-state': 'notfound' }}
        header={renderHeader(profileTitle)}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-22)',
          }}
        >
          <FormattedMessage
            id="social.profile.notfound.title"
            defaultMessage="Profile unavailable"
          />
        </h2>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
            textAlign: 'center',
            maxWidth: '320px',
          }}
        >
          <FormattedMessage
            id="social.profile.notfound.body"
            defaultMessage="This player may have deleted their account."
          />
        </p>
        <Btn kind="gold" size="md" onClick={handleBackToLobby}>
          <FormattedMessage
            id="social.profile.notfound.back_cta"
            defaultMessage="Back to lobby"
          />
        </Btn>
      </Screen>
    )
  }

  if (state.kind === 'error') {
    return (
      <Screen
        name="profile"
        gutter={18}
        contentStyle={{ gap: 'var(--space-4)', alignItems: 'center' }}
        mainProps={{ 'data-state': 'error' }}
        header={renderHeader(profileTitle)}
      >
        <p
          role="alert"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-primary)',
            textAlign: 'center',
          }}
        >
          <FormattedMessage
            id="error.server_error.body"
            defaultMessage="The runes are slow today. Try again in a moment."
          />
        </p>
      </Screen>
    )
  }

  const profile = state.profile as PublicProfileResponse
  const isSelf = profile.is_self === true
  const xpPct =
    profile.xp_to_next > 0
      ? Math.max(
          0,
          Math.min(
            100,
            (profile.xp_current / (profile.xp_current + profile.xp_to_next)) *
              100,
          ),
        )
      : 0

  const ladderKeys = LADDER_KEYS_BY_LOCALE[locale] ?? LADDER_KEYS_BY_LOCALE.en
  const perLadderElos = profile.per_ladder_elos

  const selfAtCap = activeMatchCount >= activeMatchCap
  const challengeDisabled = selfAtCap

  return (
    <Screen
      name="profile"
      gutter={18}
      contentStyle={{ gap: 'var(--space-5)', alignItems: 'center' }}
      mainProps={{
        'data-state': isSelf ? 'self' : 'public',
        'data-user-id': profile.user_uuid,
      }}
      header={renderHeader(isSelf ? profileTitle : profile.display_name)}
    >
      <section
        aria-label={intl.formatMessage({
          id: isSelf
            ? 'social.profile.section.header_self'
            : 'social.profile.section.header_other',
          defaultMessage: isSelf
            ? 'Your profile header'
            : 'Player profile header',
        })}
        data-section="profile-header"
        className="card"
        style={{
          // `.card` owns bg/radius/shadow; override only the roomier padding.
          width: '100%',
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          alignItems: 'center',
        }}
      >
        {/* Folk identity hero (prototype ProfileScreen): the size-84 Avatar leads
            the header above the name + level chip. Decorative (aria-hidden) —
            the display name below carries the accessible identity. */}
        <Avatar
          initial={profile.display_name.slice(0, 1).toUpperCase()}
          size={84}
          color={isSelf ? 'var(--color-player-a)' : 'var(--color-player-b)'}
        />
        {/* Visual hero name — the page <h1> is the back-header title, so this
            prominent display name is a styled <p> (testid preserved). */}
        <p
          data-testid="profile-display-name"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-22)',
            lineHeight: 1.1,
            color: 'var(--color-text-primary)',
            textAlign: 'center',
          }}
        >
          {profile.display_name}
        </p>
        <Chip variant="gold">
          <FormattedMessage
            id="social.profile.level"
            defaultMessage="Level {n}"
            values={{ n: profile.level }}
          />
        </Chip>
        {isSelf ? (
          <Btn kind="primary" size="md" onClick={handleEditClick}>
            <FormattedMessage
              id="social.profile.edit_name_cta"
              defaultMessage="Edit name"
            />
          </Btn>
        ) : (
          // Kept raw on the `.btn` recipe: exposes a test-asserted
          // `aria-disabled` state (ProfileScreen.test) the Btn primitive does
          // not model. Consumes the recipe, does not reimplement it.
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleChallengeOpen}
            disabled={challengeDisabled}
            data-cta="profile-challenge"
            data-disabled={challengeDisabled ? 'true' : 'false'}
            aria-disabled={challengeDisabled ? 'true' : 'false'}
            title={
              challengeDisabled
                ? intl.formatMessage({
                    id: 'social.error.challenge_self_cap',
                    defaultMessage: "You've reached your match cap.",
                  })
                : undefined
            }
          >
            <FormattedMessage
              id="social.profile.challenge_cta"
              defaultMessage="Challenge"
            />
          </button>
        )}
      </section>

      <section
        aria-label={intl.formatMessage({
          id: 'social.profile.section.xp',
          defaultMessage: 'Experience progress',
        })}
        data-section="profile-xp"
        className="card"
        style={{
          // `.card` owns bg/radius/shadow + space-4 padding.
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        <span
          data-testid="profile-xp-label"
          style={{
            fontFamily: 'var(--font-family-body)',
            fontWeight: 500,
            fontSize: 'var(--font-size-14)',
            color: 'var(--color-text-secondary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <FormattedMessage
            id="social.profile.xp_progress"
            defaultMessage="{current} / {next} XP"
            values={{
              current: <FormattedNumber value={profile.xp_current} />,
              next: (
                <FormattedNumber
                  value={profile.xp_current + profile.xp_to_next}
                />
              ),
            }}
          />
        </span>
        <div
          role="progressbar"
          aria-valuenow={Math.round(xpPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={intl.formatMessage({
            id: 'social.profile.section.xp',
            defaultMessage: 'Experience progress',
          })}
          style={{
            width: '100%',
            height: 'var(--space-2)',
            backgroundColor: 'var(--color-surface-deep)',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden',
          }}
        >
          <div
            data-testid="profile-xp-fill"
            style={{
              width: `${xpPct}%`,
              height: '100%',
              backgroundColor: 'var(--color-cta-primary)',
            }}
          />
        </div>
      </section>

      <section
        aria-label={intl.formatMessage({
          id: isSelf
            ? 'social.profile.section.ladders_self'
            : 'social.profile.section.ladders_other',
          defaultMessage: isSelf ? 'Your ladders' : 'Player ladders',
        })}
        data-section="profile-ladders"
        className="card"
        style={{
          // `.card` owns bg/radius/shadow + space-4 padding.
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-16)',
          }}
        >
          <FormattedMessage
            id="social.profile.elo.title"
            defaultMessage="Your ladders"
          />
        </h2>
        <div
          data-testid="profile-ladder-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 'var(--space-2)',
          }}
        >
          {ladderKeys.map((ladderKey) => {
            const entry = perLadderElos?.[ladderKey] ?? null
            return (
              <LadderEloCard
                key={ladderKey}
                ladderKey={ladderKey}
                entry={entry}
              />
            )
          })}
        </div>
      </section>

      <AchievementsGrid unlockedIds={profile.achievements} />

      <InventoryGrid ownedIds={profile.inventory} />

      {isSelf && (
        <section
          aria-label={intl.formatMessage({
            id: 'social.profile.section.active_matches',
            defaultMessage: 'Active matches',
          })}
          data-section="profile-active-matches"
          className="card"
          style={{
            // `.card` owns bg/radius/shadow; override to the slimmer row padding.
            width: '100%',
            padding: 'var(--space-3) var(--space-4)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-family-body)',
              fontSize: 'var(--font-size-14)',
              color: 'var(--color-text-secondary)',
              fontVariantNumeric: 'tabular-nums',
            }}
            data-testid="profile-active-match-chip"
          >
            <FormattedMessage
              id="social.profile.active_matches"
              defaultMessage="{count} active"
              values={{ count: activeMatchCount }}
            />
          </span>
        </section>
      )}

      {isSelf && bearer && (
        <EditNameModal
          open={editOpen}
          onClose={handleEditClose}
          currentDisplayName={profile.display_name}
          selfUserId={profile.user_uuid}
        />
      )}

      {!isSelf && (
        <FriendCodeModal open={challengeOpen} onClose={handleChallengeClose} />
      )}
    </Screen>
  )
}
